/** Short in-app notification chime (Web Audio — no external assets). */

let sharedCtx: AudioContext | null = null;
let lastPlayed = 0;
const MIN_INTERVAL_MS = 800;

function getCtx(): AudioContext | null {
  try {
    if (sharedCtx && sharedCtx.state !== 'closed') return sharedCtx;
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    sharedCtx = new Ctx();
    return sharedCtx;
  } catch {
    return null;
  }
}

function playTone(
  ctx: AudioContext,
  freq: number,
  start: number,
  duration: number,
  gain = 0.1
): void {
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.value = freq;
  g.gain.setValueAtTime(0, start);
  g.gain.linearRampToValueAtTime(gain, start + 0.015);
  g.gain.exponentialRampToValueAtTime(0.001, start + duration);
  osc.connect(g);
  g.connect(ctx.destination);
  osc.start(start);
  osc.stop(start + duration + 0.05);
}

/**
 * Play a short two-tone chime. Debounced so push + WS don't double-chime.
 * Call when the tab is open (WS connected) — covers focused-tab case where
 * OS push notifications are often silent.
 */
export function playNotificationChime(): void {
  const now = Date.now();
  if (now - lastPlayed < MIN_INTERVAL_MS) return;
  lastPlayed = now;

  const ctx = getCtx();
  if (!ctx) return;

  void ctx.resume().then(() => {
    const t = ctx.currentTime;
    playTone(ctx, 880, t, 0.09, 0.12);
    playTone(ctx, 1175, t + 0.1, 0.14, 0.1);
  });
}

/** Prime AudioContext after a user gesture so chimes aren't blocked by autoplay policy. */
export function primeNotificationAudio(): void {
  const ctx = getCtx();
  if (ctx) void ctx.resume();
}
